import { z } from 'zod';

/**
 * Le proposte di efficientamento per reparto (ADR 0138): nella pianta viva, dopo la configurazione, ogni reparto dice dove
 * il prodotto fa risparmiare tempo, con il conto fatto dal server e l'origine di ogni numero.
 */

/**
 * Il catalogo delle capacità del prodotto: ciò che il prodotto fa oggi, l'unica cosa che una proposta può offrire. È un
 * vocabolario del prodotto (come i disegni della pianta), non un elenco di settore.
 */
export const EFFICIENCY_CAPABILITIES = [
  'procedura_compiti',
  'linea_operativa',
  'file_esportato',
  'collegamento_programma',
  'reazione_evento',
  'controllo_permanente',
  'scadenze_obblighi',
  'lettura_posta',
  'assistente',
  'misura_soglia',
  'ricerca_archivio',
  'bozza_esterna',
  'agente_documenti',
  'agente_posta',
  'agente_risposte',
  'agente_report',
  'agente_verifica',
  'agente_bozze',
  'agente_assegnazione',
  'agente_professionisti',
  'agente_miglioramento',
  'sapere_del_ruolo',
  'agente_formazione',
] as const;
export type EfficiencyCapability = (typeof EFFICIENCY_CAPABILITIES)[number];

/** Da dove viene un numero del conto. */
export const VALUE_ORIGINS = ['azienda', 'progetto', 'libreria', 'titolare', 'misura', 'ipotesi'] as const;
export type ValueOrigin = (typeof VALUE_ORIGINS)[number];

export interface EfficiencyValueDto {
  valore: number;
  origine: ValueOrigin;
  /** La fonte (dato dell'azienda, libreria) o il perché dell'ipotesi. */
  perche: string | null;
}

/**
 * Le strade del costo orario, in ordine: tabella del Ministero, minimi del contratto dal CNEL con il calcolo dichiarato
 * (ADR 0191), bilancio, media ISTAT, nessuna.
 */
export const HOURLY_COST_ROUTES = ['tabella', 'minimi', 'bilancio', 'istat', 'nessuno'] as const;
export type HourlyCostRoute = (typeof HOURLY_COST_ROUTES)[number];

export interface HourlyCostDto {
  strada: HourlyCostRoute;
  /** Euro per ora lavorata; null con «nessuno». */
  valore: number | null;
  /** Il conto in parole e numeri («37.151 € l'anno ÷ 1.578 ore»). */
  formula: string;
  fonte: string;
  avvertenza: string | null;
}

export const EFFICIENCY_FIELDS = ['volte_mese', 'minuti_prima', 'minuti_dopo', 'persone'] as const;
export type EfficiencyField = (typeof EFFICIENCY_FIELDS)[number];

export interface EfficiencyRowDto {
  id: string;
  ruolo: string;
  /** Il lavoro che sparisce o si accorcia. */
  lavoro: string;
  volte_mese: EfficiencyValueDto;
  minuti_prima: EfficiencyValueDto;
  minuti_dopo: EfficiencyValueDto;
  persone: EfficiencyValueDto;
  /** volte × (minuti prima − minuti dopo) × persone ÷ 60. */
  ore_mese: number;
  costo: HourlyCostDto;
  euro_mese: number | null;
}

export interface EfficiencyQuestionDto {
  id: string;
  domanda: string;
  /** La riga a cui risponde; null per i mesi di attività della sede. */
  riga: string | null;
  campo: EfficiencyField | 'mesi';
}

export const LINK_MODES = ['file', 'collegamento', 'manuale'] as const;
export type LinkMode = (typeof LINK_MODES)[number];

export interface EfficiencyProposalDto {
  id: string;
  sede_id: string;
  reparto_id: string;
  reparto: string;
  titolo: string;
  /** Il problema del reparto, con i nomi dell'azienda. */
  problema: string;
  soluzione: { cosa: string; dati: string; automatismi: string };
  /** Con quali programmi e come: file esportato, collegamento (verificato se la connessione c'è già), a mano. */
  collegamenti: Array<{ programma: string; modo: LinkMode; verificato: boolean; cosa: string }>;
  capacita: Array<{ chiave: EfficiencyCapability; nome: string }>;
  /** Le attività del reparto (dalla pianta) che la proposta rende più efficienti. */
  attivita: string[];
  righe: EfficiencyRowDto[];
  ore_mese: number;
  /** Somma degli euro delle righe che hanno un costo orario. */
  euro_mese: number | null;
  /** false se qualche riga non ha un costo orario. */
  euro_completo: boolean;
  /** Mesi di attività della sede in un anno (12, o i mesi di apertura di una sede stagionale). */
  mesi: EfficiencyValueDto;
  euro_anno: number | null;
  /** da_confermare: c'è almeno un'ipotesi; stimata: nessuna ipotesi; misurata: numeri dai compiti veri. */
  stima: 'da_confermare' | 'stimata' | 'misurata';
  efficienza: string[];
  /** Gli obblighi citati: verificato se il cervello ha letto la norma, altrimenti «da verificare». */
  obblighi: Array<{ testo: string; norma: string | null; verificato: boolean }>;
  termini: { serve: string; avvio: string; prerequisiti: string; a_carico: string; limiti: string };
  domande: EfficiencyQuestionDto[];
  stato: 'aperta' | 'accettata' | 'rifiutata' | 'rimandata';
  /** Con «rimandata»: quando torna. */
  ripresa: string | null;
}

/**
 * Ciò che resta senza proposta in un reparto, con il perché: un'attività del reparto, o una capacità di il prodotto che lì
 * non serve. Il cervello propone tutto ciò che il prodotto offre: il resto lo dice qui.
 */
export interface EfficiencyExcludedDto {
  sede_id: string;
  reparto_id: string;
  tipo: 'attivita' | 'capacita';
  /** Il nome dell'attività, o il nome della capacità. */
  cosa: string;
  perche: string;
}

export interface PlantProposalsSiteDto {
  sede_id: string;
  /** Il cervello sta scrivendo le proposte di questa sede. */
  generating: boolean;
  ore_mese: number;
  euro_anno: number | null;
  mesi: number | null;
  errore: string | null;
}

export interface PlantProposalsDto {
  sedi: PlantProposalsSiteDto[];
  proposte: EfficiencyProposalDto[];
  /** Attività dei reparti e capacità di il prodotto senza proposta, con il perché: nulla resta senza risposta. */
  escluse: EfficiencyExcludedDto[];
  /** Il catalogo delle capacità, con il nome e cosa fa. */
  catalogo: Array<{ chiave: EfficiencyCapability; nome: string; cosa: string }>;
}

export const ProposalAnswerSchema = z.object({ decisione: z.enum(['accettata', 'rifiutata', 'rimandata']) }).strict();
export type ProposalAnswerInput = z.infer<typeof ProposalAnswerSchema>;

/** La risposta a una domanda di una proposta: il valore diventa del titolare e il conto si rifà. */
export const ProposalValueSchema = z
  .object({
    domanda: z.string().trim().min(1).max(80),
    valore: z.number().finite().min(0).max(100_000),
  })
  .strict();
export type ProposalValueInput = z.infer<typeof ProposalValueSchema>;
